/**
 * Priority word tracker — port of iOS
 * `client-native/src/features/lesson/state/priorityWordTracker.ts`.
 *
 * Tracks word status changes during a session, tagged by lesson ID, to derive
 * Priority-1 words (newly marked "learning" during THIS lesson). The feed's
 * 55%/90% regeneration uses `getNewlyLearningWords` to decide whether the user
 * marked MORE words since the first generation. In-memory only.
 */
import type { UserWordStatus } from "@shared_types";

export interface TrackedWord {
  wordKey: string;
  lessonId: number;
  newStatus: UserWordStatus;
}

// Key format: "lessonId:wordKey"
const wordChangesByLesson = new Map<string, TrackedWord>();

export const priorityWordTracker = {
  /** Record a word status change during a lesson. */
  recordWordChange(
    wordKey: string,
    lessonId: number,
    newStatus: UserWordStatus,
  ): void {
    wordChangesByLesson.set(`${lessonId}:${wordKey}`, {
      wordKey,
      lessonId,
      newStatus,
    });
  },

  /** Undo a recorded change (e.g. the optimistic write failed to persist). */
  removeWordChange(wordKey: string, lessonId: number): void {
    wordChangesByLesson.delete(`${lessonId}:${wordKey}`);
  },

  /** Words marked "learning" during a specific lesson (Priority 1). */
  getNewlyLearningWords(lessonId: number): Set<string> {
    const out = new Set<string>();
    wordChangesByLesson.forEach((change) => {
      if (change.lessonId === lessonId && change.newStatus === "learning") {
        out.add(change.wordKey);
      }
    });
    return out;
  },

  /** Whether a word was marked "learning" during a specific lesson. */
  wasMarkedInLesson(wordKey: string, lessonId: number): boolean {
    return (
      wordChangesByLesson.get(`${lessonId}:${wordKey}`)?.newStatus === "learning"
    );
  },

  /** Current tracked status of a word in a lesson, or null. */
  getWordStatus(wordKey: string, lessonId: number): UserWordStatus | null {
    return wordChangesByLesson.get(`${lessonId}:${wordKey}`)?.newStatus ?? null;
  },

  /** Clear tracked words for a specific lesson (e.g. on lesson repeat). */
  clearLesson(lessonId: number): void {
    const toDelete: string[] = [];
    wordChangesByLesson.forEach((change, key) => {
      if (change.lessonId === lessonId) toDelete.push(key);
    });
    toDelete.forEach((key) => wordChangesByLesson.delete(key));
  },

  /** Clear all tracked words (e.g. on logout / app restart). */
  clearAll(): void {
    wordChangesByLesson.clear();
  },
};
